import {
  IonPage,
  IonIcon,
  IonText,
  IonItem,
  IonContent,
  useIonAlert,
  useIonLoading,
  IonToolbar,
  IonButton,
  IonHeader,
  useIonModal,
  IonCard,
  IonTextarea,
  IonImg,
  IonInput,
  IonLabel,
  IonFooter,
} from '@ionic/react';
import {
  arrowBackOutline,
  chatboxEllipsesOutline,
  cameraOutline,
  trashOutline,
  addCircleOutline,
  documentTextOutline,
} from 'ionicons/icons';
import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CameraSource } from '@capacitor/camera';
import { useTranslation } from 'react-i18next';
import { Capacitor, Plugins } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { RestApi } from '../../services/Api';
import { CustomerAction } from '../../data/state/CustomerReducer';
import { VisitAction } from '../../data/state/VisitReducer';
import { usePhotoGallery } from '../../services/Camera';
import { useConstructor } from '../../services/Library';
import SelectOption from '../input/SelectOption';
import './ModalNote.scss';

export type Offering = {
  id: string;
  grouping_id: string;
  grouping_name: string;
  remark: string;
  grouping_sequence: number;
};

export interface ModalNoteProps {
  customer: any;
  type?: string;
  visit: any;
  onDismissDetail: (dismissParent?: boolean) => void;
}

const MAX_PHOTOS = 3;

const ModalNote: React.FC<ModalNoteProps> = (props) => {
  const { deletePhoto, deleteAllPhoto, photos, takePhoto } = usePhotoGallery();
  const { t } = useTranslation(['general']);
  const [alert] = useIonAlert();
  const dispatch = useDispatch();
  const [presentLoading, dismissLoading] = useIonLoading();
  const [noteImage, setNoteImage] = useState<Record<string | number, string>>({});
  const [imageList, setImageList] = useState<any>(props.visit.visit_image);
  const [groupingList, setGroupingList] = useState<any>(
    props.visit.grouping ?? [
      { grouping_id: '-', grouping_name: '-' },
    ],
  );
  const [deleteImage, setDeleteImage] = useState<any>([]);
  const [activeOfferingId, setActiveOfferingId] = useState<string | null>(null);
  const createId = () => Math.random().toString(36).substring(2, 15);
  const defaultOffering = (): Offering => ({
    id: createId(),
    grouping_id: groupingList[0].grouping_id,
    grouping_name: groupingList[0].grouping_name,
    grouping_sequence: 0,
    remark: "",
  });

  const [offeringList, setOfferingList] = useState<Offering[]>(
    props.visit.visitdet_offering &&
      typeof props.visit.visitdet_offering === 'object' &&
      !Array.isArray(props.visit.visitdet_offering)
      ? Object.entries(props.visit.visitdet_offering)
          .map(([id, o]: [string, any]) => ({
            id,
            grouping_id: o.grouping_id ?? '',
            grouping_name: o.grouping_name ?? '',
            grouping_sequence: o.grouping_sequence ?? 0,
            remark: o.remark ?? '',
          }))
          .sort((a, b) => a.grouping_sequence - b.grouping_sequence)
      : [defaultOffering()],
  );
  const [note, setNote] = useState<string>(props.visit.visitdet_note);
  const [baseUrl, setBaseUrl] = useState<any>('');
  const user = useSelector((state: any) => state.user.value);

  const totalPhotos = photos.length + imageList.length;
  const canAddPhoto = totalPhotos < MAX_PHOTOS;
  const captionRefs = useRef<Record<string, HTMLIonTextareaElement | null>>({});

  const massageAlert = async (status: string, body: string) => {
    alert({
      header: t(status),
      message: body,
      cssClass: `header-alert-${status}`,
      buttons: [
        { text: t('yes'), cssClass: 'alert-button-confirm', handler: () => {} },
      ],
    });
  };

  const addOffering = () =>
    setOfferingList((prev) => [...prev, defaultOffering()]);

  const deleteOffering = (id: string) =>
    setOfferingList((prev) => prev.filter((o) => o.id !== id));

  const updateRemark = (id: string, value: string) =>
    setOfferingList((prev) =>
      prev.map((o) => (o.id === id ? { ...o, remark: value } : o)),
    );

  const updateGrouping = (id: string, grouping: any) =>
    setOfferingList((prev) =>
      prev.map((o) =>
        o.id === id
          ? {
              ...o,
              grouping_id: grouping.grouping_id,
              grouping_name: grouping.grouping_name,
              grouping_sequence: grouping.grouping_sequence,
            }
          : o,
      ),
    );

  const selectImageSource = async () => {
    if (!canAddPhoto) {
      massageAlert(
        'gagal',
        'Gambar maksimal 3. Hapus gambar yang ada jika ingin mengganti.',
      );
      return;
    }
    await takePhoto(CameraSource.Camera);
  };

  const postNote = async () => {
    if (note.length < 10) {
      massageAlert('error', 'Catatan minimal 10 karakter');
      return;
    }
    await presentLoading({ message: `${t('loading')}` });
    const FormData = global.FormData;
    const formData = new FormData();
    photos.forEach((val: any, key: number) => {
      formData.append('image-' + key, val.blobData);
    });
    deleteImage.forEach((val: any) => formData.append('delete[]', val));
    photos.forEach((val: any, key: number) => {
      formData.append('note-' + key, noteImage[key] ?? '');
    });
    imageList.forEach((img: any) => {
      formData.append('note-' + img.visitdet_image_id, noteImage[img.visitdet_image_id] ?? img.visitdet_image_note ?? '');
    });
    formData.append('custid', props.customer.customer_id);
    formData.append('visnote', note);
    offeringList.forEach((offering: any) => {
      if (offering.grouping_id && offering.grouping_name) {
        formData.append('offering[' + offering.id + '][grouping_id]', offering.grouping_id);
        formData.append('offering[' + offering.id + '][grouping_name]', offering.grouping_name);
        formData.append('offering[' + offering.id + '][grouping_sequence]', String(offering.grouping_sequence));
        formData.append('offering[' + offering.id + '][remark]', offering.remark);
      }
    });
    await RestApi(`visit/update-note/${props.visit.visit_id}`, 'post', formData)
      .then(async (res: any) => {
        if (res.data.code === 200) {
          dispatch(CustomerAction.updateCustomer({ visitdet_note: note }));
          dispatch(
            VisitAction.updateVisit({
              customer_id: props.customer.customer_id,
              visit_id: props.customer.visit_id,
              update: { visitdet_note: note },
            }),
          );
          if (photos.length) deleteAllPhoto();
          props.onDismissDetail(true);
          massageAlert('success', res.data.message);
        } else {
          massageAlert('error', res.data.message);
        }
        dismissLoading();
      })
      .catch((error: any) => {
        massageAlert('error', error.message);
        dismissLoading();
      });
  };

  const [presentGroupingModal, dismissGroupingModal] = useIonModal(
    SelectOption,
    {
      title: `${t('select')} ${t('product')}`,
      items: groupingList.map((g: any) => ({
        value: g.grouping_id,
        text: g.grouping_name,
      })),
      multiple: false,
      onSelectionCancel: () => dismissGroupingModal(),
      selectedItems: [groupingList[0].grouping_id],
      onSelectionChange: (selected: string) => {
        dismissGroupingModal();
        const selectedGrouping = groupingList.find(
          (g: any) => g.grouping_id === selected,
        );
        if (activeOfferingId && selectedGrouping) {
          updateGrouping(activeOfferingId, selectedGrouping);
        }
      },
    },
  );

  const confirmDelete = (file: any, check: boolean) =>
    alert({
      message: 'Yakin ingin menghapus foto ini?',
      backdropDismiss: false,
      buttons: [
        { text: t('no'), cssClass: 'alert-button-cancel', role: 'cancel' },
        { text: t('yes'), cssClass: 'alert-button-confirm', role: 'confirm' },
      ],
      onDidDismiss: (e) => {
        if (e.detail.role === 'confirm') {
          if (check) {
            setDeleteImage((prev: any[]) => [...prev, file.visitdet_image_id]);
            setNoteImage((prev: any) => {
              const next = { ...prev };
              delete next[file.visitdet_image_id];
              return next;
            });
            setImageList((prev: any[]) =>
              prev.filter((obj: any) => obj.visitdet_image_id !== file.visitdet_image_id),
            );
          } else {
            deletePhoto(file);
          }
        }
      },
    });

  useConstructor(async () => {
    const url = await Preferences.get({ key: 'url' });
    setBaseUrl(url.value ?? '');
    setGroupingList(
      props.visit.grouping ?? [{ grouping_id: '-', grouping_name: '-' }],
    );
    if (Capacitor.isNative) {
      Plugins.App.addListener('backButton', () => {
        if (photos.length) deleteAllPhoto();
      });
    } else if (photos.length) {
      deleteAllPhoto();
    }
  });

  const photosDisplay = [...photos].reverse();
  const imageListDisplay = [...imageList].reverse();

  return (
    <IonPage className="modal-note-page">
      <IonHeader>
        <IonToolbar className="modal-note-toolbar">
          <IonButton
            slot="start"
            fill="clear"
            onClick={() => {
              if (photos.length) deleteAllPhoto();
              props.onDismissDetail(false);
            }}
          >
            <IonIcon icon={arrowBackOutline} slot="start" />
          </IonButton>
          <IonLabel className="toolbar-title">
            Catatan & Upload Gambar
          </IonLabel>
          <IonIcon icon={chatboxEllipsesOutline} className="ion-padding-start" />
        </IonToolbar>
      </IonHeader>

      <IonContent className="modal-note-content" fullscreen>
        {/* Note */}
        <div className="modal-note-section">
          <IonCard className="modal-note-card modal-note-note-card">
            <IonItem lines="none">
              <IonTextarea
                className="note-textarea"
                placeholder="Tulis catatan kunjungan (min. 10 karakter)"
                labelPlacement="floating"
                label="Catatan"
                name="note"
                onIonInput={(e: any) => setNote(e.detail.value ?? '')}
                value={note}
                minlength={10}
                counter={true}
                maxlength={500}
              />
            </IonItem>
          </IonCard>
        </div>

        {/* Photos */}
        <div className="modal-note-section">
          <h3 className="modal-note-section-title">
            Foto (opsional, max {MAX_PHOTOS})
          </h3>
          <div
            className="modal-note-upload-area"
            onClick={canAddPhoto ? selectImageSource : undefined}
          >
            <IonIcon icon={cameraOutline} size="large" color="medium" />
            <IonText>
              <p style={{ margin: '4px 0 0 0', fontWeight: 500 }}>
                Tap untuk ambil foto
              </p>
            </IonText>
            <p className="upload-count">{totalPhotos} / {MAX_PHOTOS}</p>
          </div>

          {(photosDisplay.length > 0 || imageListDisplay.length > 0) && (
            <div className="modal-note-photos">
              {photosDisplay.map((value: any, key: number) => {
                const refKey = `new-${key}`;
                return (
                  <div key={refKey} className="modal-note-photo-card">
                    <IonButton
                      fill="solid"
                      color="danger"
                      size="small"
                      className="photo-delete"
                      onClick={() => confirmDelete(value, false)}
                    >
                      <IonIcon icon={trashOutline} />
                    </IonButton>
                    <IonButton
                      fill="solid"
                      color="primary"
                      size="small"
                      className="photo-note-btn"
                      onClick={() => captionRefs.current[refKey]?.setFocus()}
                    >
                      <IonIcon icon={documentTextOutline} />
                    </IonButton>
                    <IonImg src={value.webviewPath} />
                    <div className="photo-caption">
                      <IonTextarea
                        ref={(el) => {
                          captionRefs.current[refKey] = el;
                        }}
                        placeholder="Keterangan"
                        value={noteImage[key]}
                        onIonChange={(e: any) => {
                          setNoteImage((prev: any) => ({
                            ...prev,
                            [key]: e.detail.value,
                          }));
                        }}
                        maxlength={100}
                      />
                    </div>
                  </div>
                );
              })}
              {imageListDisplay.map((value: any) => {
                const refKey = String(value.visitdet_image_id);
                return (
                  <div
                    key={refKey}
                    className="modal-note-photo-card"
                  >
                    <IonButton
                      fill="solid"
                      color="danger"
                      size="small"
                      className="photo-delete"
                      onClick={() => confirmDelete(value, true)}
                    >
                      <IonIcon icon={trashOutline} />
                    </IonButton>
                    <IonButton
                      fill="solid"
                      color="primary"
                      size="small"
                      className="photo-note-btn"
                      onClick={() => captionRefs.current[refKey]?.setFocus()}
                    >
                      <IonIcon icon={documentTextOutline} />
                    </IonButton>
                    <IonImg
                      src={
                        baseUrl +
                        '/image/visit/' +
                        user.company_id +
                        '/' +
                        value.visitdet_image_path
                      }
                    />
                    <div className="photo-caption">
                      <IonTextarea
                        ref={(el) => {
                          captionRefs.current[refKey] = el;
                        }}
                        placeholder="Keterangan"
                        value={
                          noteImage[value.visitdet_image_id] ??
                          value.visitdet_image_note ??
                          ''
                        }
                        onIonChange={(e: any) => {
                          setNoteImage((prev: any) => ({
                            ...prev,
                            [value.visitdet_image_id]: e.detail.value,
                          }));
                        }}
                        maxlength={100}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Offerings */}
        <div className="modal-note-section">
          <IonCard className="modal-note-card">
            <div className="modal-note-offering-header">
              <IonText className="section-label">
                {t('list')} {t('offering')}
              </IonText>
              <IonButton
                fill="clear"
                size="small"
                color="danger"
                className="add-offering"
                onClick={addOffering}
              >
                <IonIcon icon={addCircleOutline} />
              </IonButton>
            </div>
            {offeringList.map((offering) => (
              <div
                key={offering.id}
                className="modal-note-offering-item"
              >
                <div className="offering-row">
                  <div className="offering-fields">
                    <IonInput
                      readonly
                      label={`${t('select')} ${t('product')}*`}
                      labelPlacement="floating"
                      value={offering.grouping_name || '-'}
                      onClick={() => {
                        setActiveOfferingId(offering.id);
                        presentGroupingModal({
                          initialBreakpoint: 0.9,
                          breakpoints: [0, 0.9, 1],
                          handleBehavior: "cycle",
                          backdropDismiss: true,
                          id: "modal-grouping",
                          onWillDismiss: async () => {
                            const modalElement = document.getElementById(
                              "modal-grouping",
                            ) as HTMLIonModalElement;
                            if (modalElement) {
                              modalElement?.dismiss();
                            }
                          },
                        });
                      }}
                    />
                    <IonTextarea
                      label="Catatan"
                      labelPlacement="floating"
                      value={offering.remark}
                      onIonInput={(e: any) =>
                        updateRemark(offering.id, e.detail.value)
                      }
                      placeholder="Catatan untuk produk ini"
                      rows={2}
                    />
                  </div>
                  <IonButton
                    fill="clear"
                    color="danger"
                    size="small"
                    className="offering-delete"
                    onClick={() => deleteOffering(offering.id)}
                  >
                    <IonIcon icon={trashOutline} />
                  </IonButton>
                </div>
              </div>
            ))}
          </IonCard>
        </div>
      </IonContent>

      <IonFooter className="modal-note-footer ion-no-padding ion-no-margin">
        <IonButton expand="block" color="danger" size="small" onClick={postNote}>
          {t('submit')}
        </IonButton>
      </IonFooter>
    </IonPage>
  );
};

export default ModalNote;
